/* eslint-disable no-multi-assign */ // ok in reducers
import * as types from 'constants/action_types'
import * as pages from 'constants/pages'
import * as dt from 'constants/draft_types'

import Replyables from 'inbox/replyables'
import { CHANGE_USER_SUCCESS } from 'ducks/inAppCards/changeUser/types'

import { isSameActor, reduceActor } from 'util/actors'
import { emptyArr, last, wrapInArray } from 'util/arrays'
import {
  isReplyingAll,
  isForwarding as isDraftForwarding,
  translateDraftTypeToConversationType,
  trimEmptyDrafts,
} from 'util/draft'
import { deepCopy, getValueByPath, omit } from 'util/objects'
import {
  isHtmlEmpty,
  isValidEmail,
  removeHTMLTags,
  sizeInBytes,
} from 'util/strings'
import {
  isTwitter,
  getTwitterUsernames,
  RECIPIENT_TYPE_GROUPS,
} from 'util/ticket'
import { isReply, isTitle } from 'util/changesets'
import { isMobile } from 'util/media_query'

import * as draftActionTypes from './types'

const storage = require('util/storage')

const cleanupDrafts = drafts => {
  /* eslint-disable no-param-reassign  */
  drafts = drafts || {}
  Object.keys(drafts).forEach(id => {
    delete drafts[id].draft
    delete drafts[id].noteDraft
  })
  return drafts
  /* eslint-enable no-param-reassign */
}

const defaultState = {
  frozenDraft: null,
  replies: cleanupDrafts(storage.get('ticketDrafts')),
  notes: cleanupDrafts(storage.get('ticketNoteDrafts')),
}

const reducers = {}

// The idea here is to take whatever the default draft selector gives us , and
// save it it in redux.
// GR notes: Historically, we have always worked off of a full draft in redux.
// The add/remove recipient code relies heavily on to/cc/bcc to be in in redux .
// This is alright. Arguably, its easier to see what is going on when its in
// redux vs in a pure selector play (since you have to inspect the react
// component props to know what your draft really looks like)
//
reducers[draftActionTypes.INIT_LOCAL_REPLY_DRAFT] = (state, { data }) => {
  const { ticketId, draft } = data
  const decorated = { ...draft }
  return updateReplyDraft(state, ticketId, decorated)
}

reducers[draftActionTypes.UPDATE_LOCAL_REPLY_DRAFT] = (
  state,
  action,
  sharedState
) => {
  const { diff, ticketId } = action.data // eslint-disable-line no-shadow
  const replies = { ...state.replies }
  const ticketDraft = replies[ticketId]
    ? deepCopy(replies[ticketId])
    : buildTicketDraft(state, sharedState, ticketId)
  const newTicketDraft = { ...ticketDraft, ...diff }

  newTicketDraft.bodySizeBytes = sizeInBytes(newTicketDraft.body)

  // Use email as a fallback for recipient name
  // TODO: Multiple "to"s
  const lastRecipient = last(newTicketDraft.to)
  if (
    lastRecipient &&
    (!lastRecipient.name || lastRecipient.name === '') &&
    lastRecipient.email &&
    isValidEmail(lastRecipient.email)
  ) {
    lastRecipient.name = lastRecipient.email
  }

  replies[ticketId] = newTicketDraft
  storage.set('ticketDrafts', trimEmptyDrafts(replies))
  return {
    ...state,
    replies,
  }
}

function updateDraft(state, key, ticketId, newDraft) {
  const drafts = state[key] || {}
  const draft = deepCopy(drafts[ticketId] || {})
  const mergedDraft = { ...draft, ...newDraft }
  const mergedDrafts = { ...drafts }

  mergedDrafts[ticketId] = mergedDraft

  let storageKey
  switch (key) {
    case 'replies':
      storageKey = 'ticketDrafts'
      break
    case 'notes':
      storageKey = 'ticketNoteDrafts'
      break
    default:
      throw new Error('Invalid key')
  }
  storage.set(storageKey, trimEmptyDrafts(mergedDrafts))

  return {
    ...state,
    [key]: mergedDrafts,
  }
}

function updateReplyDraft(state, ticketId, newDraft) {
  return updateDraft(state, 'replies', ticketId, newDraft)
}

function updateNoteDraft(state, ticketId, newDraft) {
  return updateDraft(state, 'notes', ticketId, newDraft)
}

reducers[draftActionTypes.UPDATE_NOTE_DRAFT] = (state, action) => {
  const { draft, ticketId } = action.data
  return updateNoteDraft(state, ticketId, draft)
}

function replaceNoteDraft(state, ticketId, newDraft) {
  const drafts = { ...state.notes }
  drafts[ticketId] = deepCopy(newDraft)
  storage.set('ticketNoteDrafts', trimEmptyDrafts(drafts))
  return {
    ...state,
    notes: drafts,
  }
}

const mapPhone = recipient => {
  if (!recipient.phone) return recipient

  const mapped = { ...recipient }
  mapped.phone_number = recipient.phone
  delete mapped.phone
  return mapped
}

const mergeRecipient = (state, action) => {
  const { recipient, ticketId } = action.data
  const drafts = state.notes || {}
  const draft = drafts[ticketId] || {}
  const { to = [] } = draft
  const first = to[0]
  const merged = { ...first, ...recipient }
  return updateNoteDraft(state, ticketId, { ...draft, to: [mapPhone(merged)] })
}

const replaceRecipient = (state, action) => {
  const { recipient, ticketId } = action.data
  const drafts = state.notes || {}
  const draft = drafts[ticketId] || {}
  const newDraft = {
    ...draft,
    to: [mapPhone(recipient)],
  }

  return replaceNoteDraft(state, ticketId, newDraft)
}

reducers[draftActionTypes.UPDATE_NOTE_DRAFT_RECIPIENT] = (state, action) => {
  const { recipient, ticketId } = action.data
  if (!recipient) return state
  if (recipient.id) return replaceRecipient(state, action)

  const drafts = state.notes || {}
  const draft = drafts[ticketId] || {}
  const { to = [] } = draft
  const first = to[0]
  if (!first || first.id) return replaceRecipient(state, action)
  return mergeRecipient(state, action)
}

reducers[draftActionTypes.NOTE_DRAFT_BODY_CHANGED] = (state, action) => {
  const { body, ticketId } = action.data
  return updateNoteDraft(state, ticketId, { body })
}

const invalidateReplyDraft = (state, ticketId) => {
  const drafts = state.replies || {}
  const newDrafts = Object.assign({}, drafts)
  delete newDrafts[ticketId]
  storage.set('ticketDrafts', trimEmptyDrafts(newDrafts))

  return Object.assign({}, state, {
    replies: newDrafts,
  })
}

function invalidateNoteDraft(state, ticketId) {
  const drafts = { ...state.notes }
  delete drafts[ticketId]
  storage.set('ticketNoteDrafts', trimEmptyDrafts(drafts))
  return {
    ...state,
    notes: drafts,
  }
}

reducers[draftActionTypes.INVALIDATE_REPLY_DRAFT] = reducers[
  draftActionTypes.SEND_REPLY
] = reducers[draftActionTypes.SEND_REPLY_COMPLETE] = reducers[
  CHANGE_USER_SUCCESS
] = (state, action, sharedState) => {
  const { ticketId } = action.data
  return initReplyDraft(
    invalidateReplyDraft(state, ticketId),
    sharedState,
    ticketId,
    dt.REPLY_ALL,
    { forceInitRecipients: true }
  )
}

reducers[draftActionTypes.INVALIDATE_NOTE_DRAFT] = reducers[
  draftActionTypes.SEND_NOTE
] = (state, action) => {
  const { ticketId } = action.data
  return invalidateNoteDraft(state, ticketId)
}

reducers[draftActionTypes.UPDATE_REPLY_DRAFT_RECIPIENTS] = reducers[
  draftActionTypes.UPDATE_MULTIPLE_DRAFT_RECIPIENTS
] = (state, action, sharedState) => {
  const { recipients, ticketId } = action.data

  const replies = { ...state.replies }
  const ticketDraft = replies[ticketId]
    ? deepCopy(replies[ticketId])
    : buildTicketDraft(state, sharedState, ticketId)
  const newTicketDraft = { ...ticketDraft }

  if (recipients.to) newTicketDraft.to = recipients.to.map(reduceActor)
  if (recipients.cc) newTicketDraft.cc = recipients.cc.map(reduceActor)
  if (recipients.bcc) newTicketDraft.bcc = recipients.bcc.map(reduceActor)

  replies[ticketId] = newTicketDraft
  storage.set('ticketDrafts', trimEmptyDrafts(replies))
  return {
    ...state,
    replies,
  }
}

reducers[draftActionTypes.ADD_REPLY_DRAFT_RECIPIENTS] = (
  state,
  action,
  sharedState
) => {
  const { recipients, ticketId } = action.data
  const replies = { ...state.replies }
  const ticketDraft = deepCopy(replies[ticketId] || {})
  const newTicketDraft = { ...ticketDraft }

  RECIPIENT_TYPE_GROUPS.forEach(type => {
    newTicketDraft[type] = newTicketDraft[type] || []
    const typeArray = newTicketDraft[type]
    const typeUser = recipients[type]
    if (typeUser && !findActor(typeUser, typeArray)) {
      typeArray.push(reduceActor(typeUser))
    }
  })

  if (newTicketDraft.to.length === 0 && !ticketDraft.isForwarding) {
    const customer = sharedState.tickets.byId[ticketId].customer
    if (customer) {
      newTicketDraft.to.push(reduceActor(customer))
    }
  }

  replies[ticketId] = newTicketDraft
  storage.set('ticketDrafts', trimEmptyDrafts(replies))

  return {
    ...state,
    replies,
  }
}

function findActor(actor, collection = emptyArr) {
  return actor && collection.find(a => isSameActor(actor, a))
}

reducers[draftActionTypes.REMOVE_REPLY_DRAFT_RECIPIENTS] = (state, action) => {
  const { recipients, ticketId } = action.data
  const replies = { ...state.replies }
  const ticketDraft = deepCopy(replies[ticketId] || {})
  const newTicketDraft = { ...ticketDraft }

  RECIPIENT_TYPE_GROUPS.forEach(type => {
    newTicketDraft[type] = newTicketDraft[type] || []
    const typeArray = wrapInArray(newTicketDraft[type])
    const typeUser = recipients[type]

    if (typeUser) {
      newTicketDraft[type] = typeArray.filter(
        actor => !isSameActor(typeUser, actor)
      )
    }
  })

  replies[ticketId] = newTicketDraft
  storage.set('ticketDrafts', trimEmptyDrafts(replies))

  return {
    ...state,
    replies,
  }
}

function updateFromTicket(state, sharedState, ticket, options) {
  if (!ticket || !ticket.id) return state
  const replies = state.replies || {}

  // TODO (jscheel): This needs to be extracted with normalizr.
  if (ticket.drafts && ticket.drafts[0]) {
    const ticketDraft = replies[ticket.id] || {}
    const newTicketDraft = Object.assign({}, ticketDraft)
    newTicketDraft.body = ticket.drafts[0].body
    replies[ticket.id] = newTicketDraft
  }

  // TODO (jscheel): This seems sketch
  delete ticket.drafts // eslint-disable-line no-param-reassign

  return initReplyDraftRecipients(
    { ...state, replies },
    sharedState,
    ticket.id,
    options
  )
}

reducers[types.FETCH_TICKET_SUCCESS] = (state, action, sharedState) => {
  const data = action.data
  const ticket = data.ticket

  return updateFromTicket(state, sharedState, ticket, {
    actionType: types.FETCH_TICKET_SUCCESS,
  })
}

reducers[types.ATTACHMENT_UPLOAD_FINISHED] = (state, action, sharedState) => {
  const { ticketId, file, url, key, forNote, expiringUrl } = action.data

  if (forNote) {
    const notes = { ...state.notes }
    const noteDraft = notes[ticketId] || {}
    const attachments = noteDraft.attachments || {}
    attachments[file.name] = {
      url,
      expiringUrl,
      filename: file.name,
      file_size: file.size,
      content_type: file.type,
      key,
    }
    const newNoteDraft = {
      ...noteDraft,
      attachments,
    }
    notes[ticketId] = newNoteDraft
    storage.set('ticketNoteDrafts', trimEmptyDrafts(notes))
    return {
      ...state,
      notes,
    }
  }

  const replies = { ...state.replies }
  const ticketDraft = replies[ticketId]
    ? deepCopy(replies[ticketId])
    : buildTicketDraft(state, sharedState, ticketId)
  const attachments = ticketDraft.attachments || {}
  attachments[file.name] = {
    url,
    expiringUrl,
    filename: file.name,
    file_size: file.size,
    content_type: file.type,
    key,
  }
  const newTicketDraft = {
    ...ticketDraft,
    attachments,
  }
  replies[ticketId] = newTicketDraft

  storage.set('ticketDrafts', trimEmptyDrafts(replies))

  return {
    ...state,
    replies,
  }
}

reducers[types.DELETE_ATTACHMENT] = (state, action, sharedState) => {
  const { ticketId, file, forNote } = action.data

  if (forNote) {
    const notes = { ...state.notes }
    const noteDraft = deepCopy(notes[ticketId])
    const attachments = noteDraft.attachments || {}

    delete attachments[file.filename]

    const newNoteDraft = {
      ...noteDraft,
      attachments,
    }
    notes[ticketId] = newNoteDraft

    storage.set('ticketNoteDrafts', trimEmptyDrafts(notes))

    return {
      ...state,
      notes,
    }
  }
  const replies = { ...state.replies }
  const ticketDraft = replies[ticketId]
    ? deepCopy(replies[ticketId])
    : buildTicketDraft(state, sharedState, ticketId)
  const attachments = ticketDraft.attachments || {}
  delete attachments[file.filename]

  const newTicketDraft = {
    ...ticketDraft,
    attachments,
  }
  replies[ticketId] = newTicketDraft
  storage.set('ticketDrafts', trimEmptyDrafts(replies))
  return {
    ...state,
    replies,
  }
}

reducers[pages.TICKET_REPLY_PAGE] = reducers[
  pages.TICKET_REPLY_CHANGESET_PAGE
] = handleReplyOrForwardDraftPage(dt.REPLY_ALL)

reducers[pages.TICKET_REPLY_DIRECT_PAGE] = reducers[
  pages.TICKET_REPLY_DIRECT_CHANGESET_PAGE
] = handleReplyOrForwardDraftPage(dt.REPLY_DIRECT)

reducers[pages.TICKET_FORWARD_PAGE] = reducers[
  pages.TICKET_FORWARD_CHANGESET_PAGE
] = handleReplyOrForwardDraftPage(dt.FORWARD)

reducers[pages.TICKET_COMMENT_PAGE] = handleNoteDraftPage()

// When the mailboxes are loaded, and if we are on the new tickets page, and
// have a mailbox query param e.g. tickets/new?mailbox=help@groovehq.com then
// we need to resolve the given query param to a mailboxId, saving it on the
// draft
reducers[types.UPDATE_APP_DATA] = (state, action) => {
  const {
    mailboxes,
    path,
    queryParams: { mailbox: mailboxParam },
  } = action.data

  if (!path || path !== '/tickets/new-conversation2') return state
  if (!mailboxParam) return state

  const mailbox = (mailboxes || []).find(mbox => mbox.email === mailboxParam)

  if (!mailbox) return state

  const replies = { ...state.replies }
  const newTicketDraft = deepCopy(replies.new)

  newTicketDraft.mailboxId = mailbox.id
  replies.new = newTicketDraft
  storage.set('ticketDrafts', trimEmptyDrafts(replies))

  return {
    ...state,
    replies,
  }
}

reducers[pages.TICKET_PAGE] = (state, data, sharedState) => {
  // on desktop, its the same as a reply page
  if (!isMobile())
    return handleReplyOrForwardDraftPage(dt.REPLY_ALL)(state, data, sharedState)

  // on mobile, we have various janky jankness...
  const {
    payload: { id },
  } = data

  return {
    ...removeEmptyDraftReply(removeEmptyDraftNote(state, id), id),
    frozenDraft: null,
    isDraftDirty: undefined,
  }
}

reducers[pages.FOLDER_PAGE] = reducers[pages.MAILBOX_FOLDER_PAGE] = reducers[
  pages.MAILBOX_PAGE
] = reducers[pages.MAIN_PAGE] = reducers[pages.SEARCH_PAGE] = state => {
  return {
    ...state,
    frozenDraft: null,
  }
}

reducers[types.FETCH_TICKET_ACTIONS_SUCCESS] = (state, action, sharedState) => {
  const { ticketId } = action.data
  const draft = getReplyDraft(state, ticketId)

  // The following rely on the data in ticket.actions:
  //
  // 1. cc/bcc recipient logic for email tickets
  // 2. @mention logic for twitter tickets
  //
  const ticket = sharedState.tickets.byId && sharedState.tickets.byId[ticketId]
  if (draft && isTwitter(ticket)) {
    return initTwitterDraft(state, sharedState, ticketId)
  }

  // Only for reply all
  if (draft && isReplyingAll(draft.type)) {
    return initReplyDraftRecipients(state, sharedState, ticketId)
  }

  return state
}

reducers[types.SEARCH_SUCCESS] = reducers[types.MERGEABLE_SEARCH_SUCCESS] = (
  state,
  action,
  sharedState
) => {
  const data = action.data
  const { tickets } = data
  let newState = state

  tickets.forEach(ticket => {
    newState = updateFromTicket(newState, sharedState, ticket, {
      actionType: action.type,
    })
  })

  return newState
}

function applyChangeset(state, data) {
  const { actions, ticketId, preserveDraft, ticketData } = data
  if (!preserveDraft) {
    if (actions.find(isReply)) {
      const replies = state.replies || {}
      return {
        ...state,
        replies: omit([ticketId], replies),
      }
    }
    if (actions.find(isTitle) && state.replies && state.replies[ticketId]) {
      return {
        ...state,
        replies: {
          ...state.replies,
          [ticketId]: {
            ...state.replies[ticketId],
            title: ticketData.title,
          },
        },
      }
    }
  }
  return state
}

reducers[types.CREATE_CHANGESET_REQUEST] = reducers[types.ADD_CHANGESET] = (
  state,
  { data }
) => {
  if (!data || !data.ticketId || !data.ticketData) return state
  return applyChangeset(state, data)
}

reducers[types.CREATE_CHANGESET_SUCCESS] = (state, { data }) => {
  if (!data || !data.ticketId || !data.ticketData) return state
  const newState = applyChangeset(state, data)

  return { ...newState, frozenDraft: null }
}

reducers[types.CREATE_BULK_CHANGESET_REQUEST] = (state, { data }) => {
  if (!data || !data.tickets) return state
  let newState = Object.assign({}, state)
  const single = reducers[types.CREATE_CHANGESET_REQUEST]
  data.tickets.forEach(ticketData => {
    newState = single(newState, { data: ticketData })
  })
  return newState
}

// Apply bulk actions with a payload that has multiple tickets.
reducers[types.STAR_TICKETS_STARTED] = reducers[
  types.SNOOZE_TICKETS_REQUEST
] = (state, action) => {
  if (!action.data && !action.data.tickets) return state
  const { tickets } = action.data
  let newState = { ...state }
  tickets.forEach(ticket => {
    newState = applyChangeset(newState, ticket)
  })
  return newState
}

// When we kick off an Undo Send request, we do a two things. 1. We apply the
// optimistic UndoSend changeset that suppresses the (possibly in-flight)
// Message changeset, and 2. We restore the draft associated with this undo
reducers[types.UNDO_SEND_REQUEST] = reducers[types.UNDO_SEND_SUCCESS] = (
  state,
  action
) => {
  const { undoSendDraft } = action.data
  // we *DONT* use the ticketId off the action, but off of the draft itself.
  // This is in case its a new email draft, whose ID is actually 'new'.
  const { ticketId } = undoSendDraft
  return updateReplyDraft(state, ticketId, undoSendDraft)
}

// Removes an empty draft (from the store and LStorage). Can be either a Reply
// or a Note draft, as indicated by the draftsKey (e.g. 'replies') and
// invalidateFn (e.g. invalidateReplyDraft) arguments
function removeEmptyDraft(state, ticketId, draftsKey, invalidateFn) {
  const drafts = state[draftsKey] || {}
  const draft = drafts[ticketId]
  if (!draft) return state

  const body = draft && draft.body
  const attachments = draft && draft.attachments

  // If nothing edited, discard the draft note.
  if (ticketId && (!body || isHtmlEmpty(body)) && !attachments) {
    return invalidateFn({ ...state }, ticketId)
  }

  // Do nothing
  return state
}

function removeEmptyDraftNote(state, ticketId) {
  return removeEmptyDraft(state, ticketId, 'notes', invalidateNoteDraft)
}

function removeEmptyDraftReply(state, ticketId) {
  return removeEmptyDraft(state, ticketId, 'replies', invalidateReplyDraft)
}

// Assumes all non-empty drafts already removed!
function freezeNonEmptyDraft(state, ticketId) {
  const notes = state.notes || {}
  const noteDraft = notes[ticketId]
  const drafts = state.replies || {}
  const draft = drafts[ticketId]

  // Do nothing
  return {
    ...state,
    frozenDraft: {
      id: ticketId,
      replyDraftBodyPlainText: draft ? removeHTMLTags(draft.body) : null,
      noteDraftBodyPlainText: noteDraft ? removeHTMLTags(noteDraft.body) : null,
    },
  }
}

// Sets up the draft type and default recipients when you hit a page.
function initReplyDraft(
  state,
  sharedState,
  ticketId,
  type,
  { changesetId = null, forceInitRecipients, actionType } = {}
) {
  const ticket = sharedState.tickets.byId && sharedState.tickets.byId[ticketId]

  if (isTwitter(ticket)) return initTwitterDraft(state, sharedState, ticketId)

  const drafts = state.replies || {}
  const draft = drafts[ticketId] || {}
  const isForwarding = isDraftForwarding(type)
  const newDraft = {
    ...draft, // preserves any changes you've made
    id: ticketId, // needed if no draft yet.
    isForwarding, // DEPRECATED. use draft.type not draft.isForwarding.
    type,
    conversationType: translateDraftTypeToConversationType(type),
  }

  if (!forceInitRecipients) {
    const typeChanged = type !== draft.type
    if (!typeChanged) return updateReplyDraft(state, ticketId, newDraft)
  }

  if (!ticket) return updateReplyDraft(state, ticketId, newDraft)

  const changesetsById = sharedState.tickets.changesetsById || {}
  const changesets = changesetsById[ticketId]
  const actions = changesets ? changesets.actions : []
  const replyables = Replyables.fromTicket(
    { ...ticket, actions: { records: actions } },
    changesetId
  )

  if (isForwarding) {
    let payload
    const {
      location: { payload: { id: currentTicketId } = {} } = {},
    } = sharedState
    // HACK (jscheel && mbunsch): There are a few instances where we do not want
    // to re-initialize the reply draft when forwarding.
    if (
      actionType === types.SEARCH_SUCCESS ||
      (actionType === types.FETCH_TICKET_SUCCESS &&
        currentTicketId === ticketId)
    ) {
      payload = { ...newDraft }
    } else {
      payload = {
        ...newDraft,
        ...replyables,
        to: undefined,
        cc: undefined,
        bcc: undefined,
      }
    }
    return updateReplyDraft(state, ticketId, payload)
  }

  // NOTE: On initial page load, only the TO: field is populated until the ticket
  // actions are fully loaded. Refer to FETCH_TICKET_ACTIONS_SUCCESS.
  if (isReplyingAll(type)) {
    return updateReplyDraft(state, ticketId, {
      ...newDraft,
      ...replyables,
    })
  }

  // Replying Direct
  return updateReplyDraft(state, ticketId, {
    ...newDraft,
    ...replyables,
    cc: undefined,
    bcc: undefined,
  })
}

function initTwitterDraft(state, sharedState, ticketId) {
  const ticket = sharedState.tickets.byId && sharedState.tickets.byId[ticketId]
  const drafts = state.replies || {}
  const draft = drafts[ticketId] || {}

  const changesetsById = sharedState.tickets.changesetsById || {}
  const changesets = changesetsById[ticketId]
  const actions = changesets ? changesets.actions : []
  const fauxTicket = {
    ...ticket,
    actions: { records: actions },
  }
  const replyables = Replyables.fromTicket(fauxTicket)

  const usernames = getTwitterUsernames(fauxTicket)
  const withSpaces =
    usernames.length > 0 ? `${usernames.join(' ').trim()} ` : null

  return updateReplyDraft(state, ticketId, {
    id: ticketId, // needed if no draft yet.
    ...replyables,
    body: withSpaces,
    ...draft, // preserves any changes you've made
  })
}

function initNoteDraft(state, ticketId) {
  const drafts = state.notes || {}
  const draft = drafts[ticketId] || {}
  const newDraft = {
    ...draft, // preserves any changes you've made
    id: ticketId, // needed if no draft yet.
    conversationType: translateDraftTypeToConversationType(dt.NOTE),
    type: dt.NOTE,
  }
  return updateNoteDraft(state, ticketId, newDraft)
}

function getReplyDraft(state, ticketId) {
  return getValueByPath(`replies.${ticketId}`, state)
}

// Initializes draft if not already present (default type of REPLY_ALL)
// and calculates recipients based on what is in store.tickets.byId.
function initReplyDraftRecipients(state, sharedState, ticketId, options) {
  if (!ticketId) return state
  const draft = getReplyDraft(state, ticketId) || {}
  const draftType = draft.type || dt.REPLY_ALL

  return initReplyDraft(state, sharedState, ticketId, draftType, {
    forceInitRecipients: true,
    ...options,
  })
}

function handleReplyOrForwardDraftPage(draftType) {
  return (state, { meta, payload: { id, changesetId } }, sharedState) => {
    // HACK (jscheel): We should not be to clearing drafts when simply showing
    // or hiding fields in the form, or when a user clicks on the same path
    // that we are already on. Also, we use getValueByPath because the location
    // object is hard to pin down.
    let newState = state
    if (
      getValueByPath('location.prev.type', meta) !==
        getValueByPath('location.current.type', meta) ||
      getValueByPath('location.prev.payload.id', meta) !==
        getValueByPath('location.current.payload.id', meta)
    ) {
      newState = removeEmptyDraftReply(removeEmptyDraftNote(state, id), id)
    }
    // END HACK

    return {
      ...initReplyDraft(
        freezeNonEmptyDraft(newState, id),
        sharedState,
        id,
        draftType,
        { changesetId }
      ),
    }
  }
}

function handleNoteDraftPage() {
  return (state, { payload: { id } }) => {
    const newState = {
      ...initNoteDraft(
        freezeNonEmptyDraft(removeEmptyDraftNote(state, id), id),
        id
      ),
    }
    return newState
  }
}

function buildTicketDraft(state, sharedState, ticketId) {
  const isNew = ticketId === 'new'
  const ticket = isNew ? {} : sharedState.tickets.byId[ticketId] || {}

  return {
    to: ticket.customer,
  }
}

export default function reducer(
  state = defaultState,
  action,
  sharedState = {}
) {
  // this is here because a long reducer with many ifs is unreadable
  const handler = reducers[action.type]
  if (handler) return handler(state, action, sharedState)
  return state
}
